import { useEffect, useMemo, useState } from 'react';
import { sanityClient } from '../lib/sanityClient';
import { ALL_UAE_TOURS, UAE_TOUR_BY_SLUG } from '../lib/queries';
import { urlFor } from '../lib/urlFor';

export interface SanityImageRef {
  _type: 'image';
  asset: { _ref: string; _type: 'reference' };
  alt?: string;
}

export interface UaeTourFaq {
  q?: string;
  a?: string;
  question?: string;
  answer?: string;
}

export interface UaeTourItineraryItem {
  title?: string;
  description?: string;
  time?: string;
}

export interface SanityUaeTour {
  _id: string;
  title: string;
  slug: { current: string };
  category?: string;
  featuredImage?: SanityImageRef;
  gallery?: SanityImageRef[];
  shortDescription?: string;
  fullDescription?: string;
  startingPrice?: number;
  priceLabel?: string;
  duration?: string;
  pickupIncluded?: boolean;
  pickupLocation?: string;
  dropoffIncluded?: boolean;
  tourType?: string;
  bestFor?: string[];
  highlights?: string[];
  inclusions?: string[];
  exclusions?: string[];
  itinerary?: UaeTourItineraryItem[];
  thingsToKnow?: string[];
  cancellationPolicy?: string;
  faqs?: UaeTourFaq[];
  metaTitle?: string;
  metaDescription?: string;
  featured?: boolean;
  popular?: boolean;
  displayOrder?: number;
  whatsappMessage?: string;
}

export interface UaeTourView {
  id: string;
  title: string;
  slug: string;
  category: string;
  image: string;
  gallery: string[];
  shortDescription: string;
  fullDescription: string;
  startingPrice?: number;
  priceLabel: string;
  duration: string;
  pickupIncluded: boolean;
  pickupLocation: string;
  dropoffIncluded: boolean;
  tourType: string;
  bestFor: string[];
  highlights: string[];
  inclusions: string[];
  exclusions: string[];
  itinerary: UaeTourItineraryItem[];
  thingsToKnow: string[];
  cancellationPolicy: string;
  faqs: { q: string; a: string }[];
  metaTitle?: string;
  metaDescription?: string;
  featured: boolean;
  popular: boolean;
  displayOrder: number;
  whatsappMessage: string;
}

const CATEGORY_MAP: Record<string, string> = {
  Safari: 'Desert Safari',
  Tours: 'Dubai Tours',
  Cruise: 'Cruise & Yacht',
  Tickets: 'Theme Park Tickets',
  Adventure: 'Adventure Tours',
};

export function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizeUaeTourCategory(category?: string) {
  if (!category) return 'Dubai Tours';
  return CATEGORY_MAP[category] ?? category;
}

export function mapSanityUaeTour(tour: SanityUaeTour): UaeTourView {
  const image = tour.featuredImage ? urlFor(tour.featuredImage).width(900).height(620).fit('crop').url() : '';
  const gallery = (tour.gallery ?? []).map(img => urlFor(img).width(900).height(620).fit('crop').url());

  return {
    id: tour._id,
    title: tour.title,
    slug: tour.slug?.current ?? slugify(tour.title),
    category: normalizeUaeTourCategory(tour.category),
    image,
    gallery,
    shortDescription: tour.shortDescription ?? 'Private and shared UAE tour experience with Big Bird Travel support.',
    fullDescription: tour.fullDescription ?? tour.shortDescription ?? 'Enjoy a carefully arranged UAE tour with clear timing, supplier coordination, and WhatsApp support from our travel team.',
    startingPrice: tour.startingPrice,
    priceLabel: tour.priceLabel ?? 'From',
    duration: tour.duration ?? 'Flexible',
    pickupIncluded: tour.pickupIncluded ?? true,
    pickupLocation: tour.pickupLocation ?? 'Hotel pickup available',
    dropoffIncluded: tour.dropoffIncluded ?? true,
    tourType: tour.tourType ?? 'Shared / Private',
    bestFor: tour.bestFor ?? [],
    highlights: tour.highlights ?? [],
    inclusions: tour.inclusions ?? [],
    exclusions: tour.exclusions ?? [],
    itinerary: tour.itinerary ?? [],
    thingsToKnow: tour.thingsToKnow ?? [],
    cancellationPolicy: tour.cancellationPolicy ?? 'Cancellation terms vary by activity and supplier. Contact us before booking for the latest policy.',
    faqs: (tour.faqs ?? [])
      .map(faq => ({ q: faq.q ?? faq.question ?? '', a: faq.a ?? faq.answer ?? '' }))
      .filter(faq => faq.q && faq.a),
    metaTitle: tour.metaTitle,
    metaDescription: tour.metaDescription,
    featured: tour.featured ?? false,
    popular: tour.popular ?? false,
    displayOrder: tour.displayOrder ?? 999,
    whatsappMessage: tour.whatsappMessage ?? `Hi Big Bird Travel, I want to book ${tour.title}. Please share availability and price.`,
  };
}

export function useUaeTours() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tours, setTours] = useState<UaeTourView[]>([]);

  useEffect(() => {
    sanityClient
      .fetch<SanityUaeTour[]>(ALL_UAE_TOURS)
      .then(data => setTours((data ?? []).map(mapSanityUaeTour)))
      .catch(err => setError((err as Error).message ?? 'Failed to load UAE tours'))
      .finally(() => setLoading(false));
  }, []);

  return useMemo(() => ({ loading, error, tours }), [loading, error, tours]);
}

export function useUaeTour(slug?: string) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tour, setTour] = useState<UaeTourView | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    sanityClient
      .fetch<SanityUaeTour | null>(UAE_TOUR_BY_SLUG, { slug })
      .then(data => setTour(data ? mapSanityUaeTour(data) : null))
      .catch(err => setError((err as Error).message ?? 'Failed to load UAE tour'))
      .finally(() => setLoading(false));
  }, [slug]);

  return useMemo(() => ({ loading, error, tour }), [loading, error, tour]);
}
